/**
 * record-filter-chips.js
 */

import React from 'react';
import { Button as MuiButton, Chip, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useRouteMatch, useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getFilterParamsString,
  getFilterParamsObj,
  getParamSummary,
} from 'utils/filter-params';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import Icon from 'components/icon';
import { useFilterCriteriaSpecs } from 'routing/routes/records/filter-dialog/filter-criteria-specs';


const useStyles = makeStyles(theme => ({
  root: {
    margin: theme.spacing(-0.5),

    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  filterChip: {
    maxWidth: 180,
  },
  clearButton: {
    marginLeft: theme.spacing(1),
  },
}));


export default function RecordFilterChips(props) {
  const { searchOptions, showClear } = props;
  const classes = useStyles(); 
  const { t } = useTranslation('general'); 
  const criteriaSpecs = useFilterCriteriaSpecs(t, searchOptions); 
  const { params } = useRouteMatch();
  const history = useHistory();
  const filterParamsString = params.filterParamsString;
  const filterParams = getFilterParamsObj(filterParamsString);

  const filterParamSummaries = React.useMemo(() => {
    return Object.entries(filterParams).reduce(function (summaries, [key, value]) {
      const summary = getParamSummary(key, value, filterParams, searchOptions, criteriaSpecs, t);
      if (summary) {
        summaries.push([key, summary]);
      }
      return summaries;
    }, []);
  }, [searchOptions, criteriaSpecs, filterParams, t]);


  const onDeleteParam = (param) => {
    const removeParam = (paramToRemove, params) => {
      const { [paramToRemove]: _, ...newParams } = params;
      return newParams;
    };

    let updatedFilterParams = removeParam(param, filterParams);

    // Remove other params that depend on this one
    if (param === 'dateRange') {
      updatedFilterParams = removeParam('startDate', updatedFilterParams);
      updatedFilterParams = removeParam('endDate', updatedFilterParams);
    } else if (param === 'handlers') {
      updatedFilterParams = removeParam('dogId', updatedFilterParams);
    }

    const paramString = getFilterParamsString(updatedFilterParams);
    history.push(makeLocation('', 'records', paramString ? 'params' : 'basic', {
      p1: Types.FilterMode.FILTER,
      filterParams: paramString
    }));
  };


  return filterParamSummaries.length === 0
    ? null
    : (
      <div className={classes.root}>
        {filterParamSummaries.map(([key, summary]) =>
          <Tooltip
            arrow
            title={summary.label || ''}
            key={key}
          >
            <Chip
              className={classes.filterChip}
              size='small'
              color='primary'
              onDelete={() => onDeleteParam(key)}
              label={summary.value}
            />
          </Tooltip>
        )}
        {showClear && filterParamSummaries.length > 1 &&
          <MuiButton
            className={classes.clearButton}
            component={Link}
            to={`/records`}
            color='primary'
            size='small'
            startIcon={<Icon type='filter-remove' />}
            children='Clear'
          />
        }
      </div>
    );
}